// --------------------- Import Global ---------------------
import { Dispatch, SetStateAction, useEffect, useState } from "react";
// --------------------- Import Antd-Design ---------------------
import {
  Skeleton,
  Form,
  Input,
  Select,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Checkbox,
  CheckboxProps,
} from "antd";
// --------------------- Import Hooks ---------------------
import { useCidades } from "../../../hooks/useCidades";
import { useEmpreendimentosSystem } from "../../../hooks/useEmpreendimentosSystem";
import { useApartamentoByIdEmpreend } from "../../../hooks/useApartamentoByIdEmpreend";
import { useApartamentoById } from "../../../hooks/useApartamentoById";
import { useCotasByIdApto } from "../../../hooks/useCotasByIdApto";

// --------------------- Import Styles ---------------------
import { ContainerModal, formStyle, CheckBoxContainer } from "../styles";
import { TextModal } from "../../ViewGerenciarPropriedadeUsuario/styles";
import { layout } from "../../ViewGerenciarPropriedadeUsuario/constant/layout";

// --------------------- Import Interfaces ---------------------
import { Empreendimento } from "../../../interfaces/Empreendimento";
import { Apartamento } from "../../../interfaces/Apartamento";
import { CotaUsuario } from "../../../interfaces/CotaUsuario";

// --------------------- Import Úteis ---------------------
import { filterOption, filterSort } from "../../../uteis/filtroSelect";

// --------------------- Import Constants ---------------------
import { vistaOptions } from "../../ViewGerenciarPropriedadeUsuario/constant/vistaOptions";
import { usePaises } from "../../../hooks/usePaises";
import { useEstados } from "../../../hooks/useEstados";
import { Cota } from "../../../interfaces/Cota";

interface FormCadastroInicialProps {
  setEmpreendimento: Dispatch<SetStateAction<Partial<Empreendimento>>>;
  setApartamento: Dispatch<SetStateAction<Partial<Apartamento>>>;
  setMaisInfo: Dispatch<SetStateAction<Partial<CotaUsuario>>>;
  setEmpreendimentoSistema: Dispatch<SetStateAction<boolean>>;
  setCotas: Dispatch<SetStateAction<Cota[]>>;
}

export default function FormCadastroInicial({
  setEmpreendimento,
  setApartamento,
  setMaisInfo,
  setEmpreendimentoSistema,
  setCotas,
}: FormCadastroInicialProps) {
  // Define o nome dos  formularios
  const [formCadastroInicialEmp] = Form.useForm();
  const [formCadastroInicialAp] = Form.useForm();
  const [formCadastroInicialMaisInfo] = Form.useForm();

  // Define o formulario do apartamento para vincular ao empreendimento do sistema
  const [formSelectEmpreendimentoSystem] = Form.useForm();
  const [formApartamentoBySystem] = Form.useForm();
  const [formCotasComCronograma] = Form.useForm();

  // UseStates necessários para o passo de cadastro de apartamento e busca de empreendimento cadastrado
  const [possuiVaranda, setPossuiVaranda] = useState<number>();
  const [estacionamento, setEstacionamento] = useState<number>();
  const [terreo, setTerreo] = useState<number>();

  // Estado necessário para vincular empreendimento a multicotas ou não
  const [vincularEmpreendimento, setVincularEmpreendimento] =
    useState<boolean>(false);

  // UseState para armazenar o ID do estado selecionado
  const [estadoSelecionados, setEstadoSelecionadoId] = useState<
    number | undefined
  >(undefined);

  // UseState para armazenar o ID do empreendimento selecionado
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState<
    number | undefined
  >(undefined);

  // UseState para armazenar o ID do apartamento selecionado
  const [apartamentoSelecionado, setApartamentoSelecionado] = useState<
    number | undefined
  >(undefined);

  // Use para buscar as cidades do Estado Selecionado
  const {
    isLoading: isLoadingPaises,
    data: paises,
    error: errorPaises,
  } = usePaises();

  // Use para buscar as cidades do Estado Selecionado
  const {
    isLoading: isLoadingEstados,
    data: estados,
    error: errorEstados,
  } = useEstados();

  // Use para buscar as cidades do Estado Selecionado
  const {
    isLoading: isLoadingCidades,
    data: cidades,
    error: errorCidades,
  } = useCidades(estadoSelecionados);

  // Use para buscar os empreendimentos do sistema
  const {
    isLoading: isLoadingEmpreendSystem,
    data: empreendimentosSystem,
    error: errorEmpreendSystem,
  } = useEmpreendimentosSystem();

  // Use para buscar os apartamentos do sistema Selecionado
  const {
    isLoading: isLoadingApartamentosEmpreend,
    data: apartamentosEmpreend,
    error: errorApartamentosEmpreend,
  } = useApartamentoByIdEmpreend(empreendimentoSelecionado);

  // Use para buscar as informações do apartamento do selecionado
  const {
    isLoading: isLoadingInfoApartamento,
    data: infoApartamento,
    error: errorInfoApartamento,
  } = useApartamentoById(apartamentoSelecionado);

  // Use para buscar as informações das cotas do apartemento  selecionado
  const {
    isLoading: isLoadingCotas,
    data: cotas,
    error: errorCotas,
  } = useCotasByIdApto(apartamentoSelecionado);

  useEffect(() => {
    if (cotas) {
      setCotas(cotas);
    }
  }, [cotas]);

  // Função que armazena os dados alterados de Empreendimento
  const empreendimentoChange = (
    changedValues: Partial<Empreendimento>,
    allValues: Empreendimento
  ) => {
    setEmpreendimento(allValues);
  };

  // Função que armazena os dados alterados de Apartamento
  const apartamentoChange = (changedValues: Partial<any>, allValues: any) => {
    setApartamento(allValues);
  };

  // Função que armazena os dados alterados de Mais Info
  const maisInfoChange = (
    changedValues: Partial<CotaUsuario>,
    allValues: Partial<CotaUsuario>
  ) => {
    setMaisInfo(allValues);
  };

  // Função que armazena o dado selecionado da varanda
  const possuiVarandaChange = (e: RadioChangeEvent) => {
    setPossuiVaranda(e.target.value);
  };

  // Função que armazena o dado selecionado do estacionamento
  const estacionamentoChange = (e: RadioChangeEvent) => {
    setEstacionamento(e.target.value);
  };

  // Função que armazena o dado selecionado do terreo
  const terreoChange = (e: RadioChangeEvent) => {
    setTerreo(e.target.value);
  };

  const vincularEmpreendimentoChange: CheckboxProps["onChange"] = (e) => {
    setVincularEmpreendimento(e.target.checked);
    setEmpreendimentoSistema(e.target.checked);
    // Limpa formulários ao alternar
    formCadastroInicialEmp.resetFields();
    formSelectEmpreendimentoSystem.resetFields();
  };

  const selectEmpreendimentoChange = (value: number) => {
    const selectedEmp = empreendimentosSystem?.find(
      (emp: Empreendimento) => emp.id === value
    );
    setEmpreendimentoSelecionado(value);
    // Resetar formulários vinculados
    formApartamentoBySystem.resetFields();
    formCotasComCronograma.resetFields();
  };

  const handleApartamentoInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setApartamento((prev) => ({
      ...prev,
      nome: e.target.value,
    }));
  };

  const handleEmpreendimentoInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEmpreendimento((prev) => ({
      ...prev,
      nome: e.target.value,
    }));
  };

  const handleApartamentoChange = (value: number) => {
    const aptoSelecionado = apartamentosEmpreend?.find(
      (apto) => apto.id === value
    );

    if (aptoSelecionado) {
      formApartamentoBySystem.setFieldsValue({
        capacidadeAp: aptoSelecionado.capacidade,
        quartosAp: aptoSelecionado.quartos,
        banheirosAp: aptoSelecionado.banheiros,
      });

      // Mapeia TUDO para o pai
      setApartamento({
        id: value,
        capacidade: aptoSelecionado.capacidade,
        quartos: aptoSelecionado.quartos,
        banheiros: aptoSelecionado.banheiros,
      });
    }

    setApartamentoSelecionado(value);
  };

  // Função que armazena os dados alterados de Empreendimento
  const empreendimentoSystemChange = (
    changedValues: Partial<any>,
    allValues: any
  ) => {
    setEmpreendimento(allValues);
  };

  const [resetKey, setResetKey] = useState<string>(Date.now().toString());
  return (
    <>
      {isLoadingEstados && isLoadingPaises && <Skeleton active />}
      {!isLoadingEstados && !isLoadingPaises && (
        <ContainerModal>
            <CheckBoxContainer>
            <Checkbox
              checked={vincularEmpreendimento}
              onChange={vincularEmpreendimentoChange}
              style={{ fontWeight: "bold", color: "#4A4A4A" }}
            >
              {vincularEmpreendimento
                ? "Desvincular ao Empreendimento do sistema"
                : "Vincular ao Empreendimento do sistema"}
            </Checkbox>
          </CheckBoxContainer>
          {!vincularEmpreendimento ? (
            <>
              {/* Formulário de Empreendimento */}
              <Form
                {...layout}
                form={formCadastroInicialEmp}
                onValuesChange={empreendimentoChange}
                name="empreendimento"
                style={formStyle}
              >
                <TextModal>Empreendimento</TextModal>
                <Form.Item
                  name={["nome"]}
                  label="Empreendimento:"
                  className="formItems"
                >
                  <Input onChange={handleEmpreendimentoInputChange} />
                </Form.Item>
                <Form.Item name={["paisId"]} label="País" className="formItems">
                  <Select
                    showSearch
                    className="selectCidades"
                    placeholder="Selecione o país"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    filterSort={filterSort}
                  >
                    {paises?.map((pais) => (
                      <Select.Option key={pais.id} value={pais.id}>
                        {pais.nome}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name={["estadoId"]}
                  label="Estado"
                  className="formItems"
                >
                  <Select
                    showSearch
                    className="selectCidades"
                    placeholder="Selecione o estado"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    filterSort={filterSort}
                    onChange={(value) => setEstadoSelecionadoId(value)}
                  >
                    {estados?.map((estados) => (
                      <Select.Option key={estados.id} value={estados.id}>
                        {estados.nome}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name={["cidadeId"]}
                  label="Cidade"
                  className="formItems"
                >
                  <Select
                    showSearch
                    className="selectCidades"
                    placeholder="Selecione a cidade"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    filterSort={filterSort}
                    disabled={
                      isLoadingCidades || !cidades || cidades.length === 0
                    }
                  >
                    {cidades?.map((cidade) => (
                      <Select.Option key={cidade.id} value={cidade.id}>
                        {cidade.nome}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name={["cronograma"]}
                  label="Cronograma"
                  className="formItems"
                >
                  <InputNumber min={0} />
                </Form.Item>
              </Form>

              {/* Formulário de Apartamento */}
              <Form
                {...layout}
                form={formCadastroInicialAp}
                onValuesChange={apartamentoChange}
                name="apartamento"
                style={formStyle}
              >
                <TextModal>Apartamento</TextModal>
                <Form.Item
                  name={["nome"]}
                  label="Apartamento"
                  className="formItems"
                >
                  <Input onChange={handleApartamentoInputChange} />
                </Form.Item>
                <Form.Item
                  name={["capacidade"]}
                  label="Capacidade"
                  className="formItems"
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={["quartos"]}
                  label="Quartos"
                  className="formItems"
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={["banheiros"]}
                  label="Banheiros"
                  className="formItems"
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={["metragem"]}
                  label="Metragem"
                  className="formItems"
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={["varanda"]}
                  label="Varanda"
                  className="formItems"
                >
                  <Radio.Group
                    onChange={possuiVarandaChange}
                    value={possuiVaranda}
                  >
                    <Radio value={0}>Não</Radio>
                    <Radio value={1}>Sim</Radio>
                    <Radio value={2}>Não Sei</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  name={["terreo"]}
                  label="Térreo"
                  className="formItems"
                >
                  <Radio.Group onChange={terreoChange} value={terreo}>
                    <Radio value={0}>Não</Radio>
                    <Radio value={1}>Sim</Radio>
                    <Radio value={2}>Não Sei</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  name={["estacionamento"]}
                  label="Estacionamento"
                  className="formItems"
                >
                  <Radio.Group
                    onChange={estacionamentoChange}
                    value={estacionamento}
                  >
                    <Radio value={0}>Não</Radio>
                    <Radio value={1}>Sim</Radio>
                    <Radio value={2}>Não Sei</Radio>
                  </Radio.Group>
                </Form.Item>
              </Form>
            </>
          ) : (
            <>
              <Form
                {...layout}
                form={formSelectEmpreendimentoSystem}
                onValuesChange={empreendimentoSystemChange}
                name="empreendimento-system"
                style={formStyle}
              >
                <TextModal>Empreendimento</TextModal>
                <Form.Item
                  name={["id"]}
                  label="Empreendimento"
                  className="formItems"
                >
                  <Select
                    showSearch
                    className="selectEmpreendimento"
                    placeholder="Selecione um empreendimento"
                    optionFilterProp="children"
                    onChange={selectEmpreendimentoChange}
                    filterOption={filterOption}
                    filterSort={filterSort}
                  >
                    {empreendimentosSystem?.map((emp: Empreendimento) => (
                      <Select.Option
                        key={emp.id}
                        value={emp.id}
                        label={emp.nome}
                      >
                        {emp.nome}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form>
              {/* Formulário de Apartamento */}

              <Form
                key={`${resetKey}-apto`}
                {...layout}
                form={formApartamentoBySystem}
                name="apartamento-system"
                style={formStyle}
              >
                <TextModal>Apartamento</TextModal>
                <Form.Item
                  name="nomeAp"
                  label="Apartamento "
                  className="formItems"
                >
                  <Select
                    showSearch
                    placeholder="Selecione o apartamento"
                    filterOption={filterOption}
                    filterSort={filterSort}
                    onChange={handleApartamentoChange}
                    disabled={
                      isLoadingApartamentosEmpreend ||
                      !apartamentosEmpreend ||
                      apartamentosEmpreend.length === 0
                    }
                  >
                    {apartamentosEmpreend?.map((apto: Apartamento) => (
                      <Select.Option
                        key={apto.id}
                        value={apto.id}
                        label={apto.nome}
                      >
                        {apto.nome}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="capacidadeAp"
                  label="Capacidade"
                  className="formItems"
                >
                  <Input style={{ width: "30%" }} readOnly />
                </Form.Item>
                <Form.Item
                  name="quartosAp"
                  label="Quartos"
                  className="formItems"
                >
                  <Input style={{ width: "30%" }} readOnly />
                </Form.Item>
                <Form.Item
                  name="banheirosAp"
                  label="Banheiros"
                  className="formItems"
                >
                  <Input style={{ width: "30%" }} readOnly />
                </Form.Item>
              </Form>
            </>
          )}

          {/* Formulário de Mais Informações do Apartamento */}
          <Form
            {...layout}
            form={formCadastroInicialMaisInfo}
            onValuesChange={maisInfoChange}
            name="mais-info-apto"
            style={formStyle}
          >
            <TextModal>Mais Informações do Apartamento</TextModal>
            <Form.Item
              name={["numeroApto"]}
              label="Nº Apartamento"
              className="formItems"
            >
              <Input />
            </Form.Item>
            <Form.Item name={["andar"]} label="Andar" className="formItems">
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item name={["bloco"]} label="Bloco" className="formItems">
              <Input />
            </Form.Item>
            <Form.Item name={["vista"]} label="Vista" className="formItems">
              <Select style={{ width: 150 }} options={vistaOptions} />
            </Form.Item>
          </Form>
        
        </ContainerModal>
      )}
    </>
  );
}
